import type { AgentUsageDoor } from '@travel-planner/shared/schemas/agent-usage';
import { formatUtcDate } from '@/shared/lib/format-utc-date';

export const EMPTY_CELL = '—';

export const DOOR_LABEL: Record<AgentUsageDoor | 'other', string> = {
  planner: 'Claude (consultant)',
  external: 'Agent portal',
  draft: 'Internal test',
  runtime: 'Automatic (XT)',
  other: 'Other',
};

export const doorLabel = (door: string): string =>
  DOOR_LABEL[door as AgentUsageDoor] ?? door;

/** USD with 2 decimals, 4 below a cent so tiny spends don't read as $0.00. */
export const formatUsd = (usd: number): string => {
  const digits = usd !== 0 && Math.abs(usd) < 0.01 ? 4 : 2;
  return `$${usd.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};

/** A USD amount, marked ≈ when it is an estimate. */
export const formatMoney = (
  usd: number,
  { estimate = false }: { estimate?: boolean } = {}
): string => (estimate ? approx(formatUsd(usd)) : formatUsd(usd));

export const formatTokens = (tokens: number): string =>
  Math.round(tokens).toLocaleString('en-US');

export const formatCompactTokens = (tokens: number): string => {
  const n = Math.round(tokens);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

/** Marks a figure as an estimate. */
export const approx = (text: string): string => `≈${text}`;

export const formatUsageTime = (iso: string | null): string =>
  formatUtcDate(iso, 'yyyy-MM-dd HH:mm');

/** A UTC calendar day in words, e.g. "2 Nov 2026". */
export const formatUsageDay = (iso: string | null): string =>
  formatUtcDate(iso, 'd MMM yyyy');

/** A `YYYY-MM-DD` week start as a short label, e.g. "27 Oct". */
export const formatWeek = (day: string): string =>
  formatUtcDate(`${day}T00:00:00Z`, 'd MMM', day);

export type OutcomeTone = 'success' | 'warning' | 'muted' | 'failure';

/** The run outcome codes in plain words, with a tone for the badge. */
export const OUTCOME: Record<string, { label: string; tone: OutcomeTone }> = {
  limpo: { label: 'Written', tone: 'success' },
  alerta: { label: 'Written — points to check', tone: 'warning' },
  incompleta: { label: 'Not finished', tone: 'muted' },
  nao_escrito: { label: 'Not written (refused)', tone: 'failure' },
  erro: { label: 'Error', tone: 'failure' },
};

export const outcomeOf = (
  code: string | null
): { label: string; tone: OutcomeTone } | null =>
  code === null ? null : (OUTCOME[code] ?? { label: code, tone: 'muted' });
